import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Check,
  Medal,
  Minus,
  Plus,
  Trophy,
  UserMinus,
  Users,
} from "lucide-react";
import AdminLayout from "../../components/AdminLayout";
import { useBrand } from "../../context/BrandContext";
import {
  createCompetition,
  fetchLeagueFixtureRsvps,
  fetchMyLeagueSchedule,
  fetchSeniorsTeams,
  listCompetitions,
  openChampionshipMatch,
  setClubChampionshipTeamCount,
  setCompetitionParticipation,
  setLeagueFixtureRsvp,
  updateCompetition,
} from "../../components/funtionsBD/competitionService";
import TournamentSetupComposer from "../common/TournamentSetupComposer";
import {
  availableForMatch,
  cascadeMatchSquad,
  emptySeats,
  isCalledUp,
  playersOutThisMatch,
  presenceOutsFromRsvps,
} from "../../utils/competitionLineup";
import "./CompetitionsPage.css";

const MATCH_LEAD_POSITIONS = new Set([
  "matches_results",
  "sports_director",
  "event_coordinator",
]);

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function formatMatchDate(iso, locale) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function isMatchLead(user) {
  const pos = String(user?.admin_position || user?.adminPosition || "")
    .toLowerCase()
    .trim();
  return MATCH_LEAD_POSITIONS.has(pos);
}

function Avatar({ player }) {
  if (player?.user_image) {
    return <img src={player.user_image} alt="" className="comp-ed__ava" />;
  }
  return <span className="comp-ed__ava comp-ed__ava--ini">{initials(player?.name)}</span>;
}

export default function CompetitionsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { themeMode } = useBrand();
  const locale = i18n.language || "en";
  const [user] = useState(() => readUser());
  const matchLead = isMatchLead(user);
  const mode = themeMode === "light" ? "light" : "dark";

  const [tab, setTab] = useState("championship");
  const [list, setList] = useState([]);
  const [teams, setTeams] = useState(null);
  const [teamCount, setTeamCount] = useState(0);
  const [schedule, setSchedule] = useState(null);
  const [rsvpsByFixture, setRsvpsByFixture] = useState({});
  const [presenceOutsByTeam, setPresenceOutsByTeam] = useState({});
  const [busy, setBusy] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [tourName, setTourName] = useState("");
  const [tourCategory, setTourCategory] = useState("seniors");
  const [tourKind, setTourKind] = useState("tournament");

  const lang = (locale || "en").substring(0, 2);

  const applyView = (view) => {
    setTeams(view);
    setTeamCount(
      Number(
        view?.competition?.effective_team_count ??
          view?.club?.championship_team_count ??
          0
      )
    );
  };

  const load = useCallback(async () => {
    setError("");
    try {
      const seniors = await fetchSeniorsTeams({ lang, reform: true });
      applyView(seniors);
      const [comps, sched] = await Promise.all([
        listCompetitions(),
        fetchMyLeagueSchedule({ lang }).catch(() => null),
      ]);
      setList(comps || []);
      setSchedule(sched);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    }
  }, [lang]);

  useEffect(() => {
    load();
    const onClub = () => load();
    window.addEventListener("smarttt-club-changed", onClub);
    window.addEventListener("smarttt-user-updated", onClub);
    return () => {
      window.removeEventListener("smarttt-club-changed", onClub);
      window.removeEventListener("smarttt-user-updated", onClub);
    };
  }, [load]);

  const clubTeams = useMemo(() => {
    const fromLeague = schedule?.club_teams || [];
    if (fromLeague.length) {
      return [...fromLeague].sort((a, b) => Number(a.team_number) - Number(b.team_number));
    }
    return (teams?.teams || []).map((tm) => ({
      league_team_id: `champ-${tm.team_number}`,
      team_number: tm.team_number,
      display_name: tm.label,
      division: teams?.competition?.name,
    }));
  }, [schedule, teams]);

  const championshipTeams = useMemo(() => {
    const byNumber = new Map(
      (teams?.teams || []).map((tm) => [Number(tm.team_number), tm])
    );
    if (!clubTeams.length) return teams?.teams || [];
    const maxN = Math.max(
      ...clubTeams.map((t) => Number(t.team_number) || 0),
      ...(teams?.teams || []).map((t) => Number(t.team_number) || 0),
      0
    );
    const rows = [];
    for (let n = 1; n <= maxN; n += 1) {
      const league = clubTeams.find((t) => Number(t.team_number) === n);
      const champ = byNumber.get(n);
      rows.push({
        team_number: n,
        label: league?.display_name || champ?.label || `Team ${n}`,
        players: champ?.players || [],
      });
    }
    return rows;
  }, [clubTeams, teams]);

  const today = new Date().toISOString().slice(0, 10);
  const nextByTeam = useMemo(() => {
    const fixtures = schedule?.fixtures || [];
    return clubTeams.map((team) => {
      const mine = fixtures.filter(
        (f) =>
          f.home?.league_team_id === team.league_team_id ||
          f.away?.league_team_id === team.league_team_id
      );
      const next = mine.find((f) => String(f.match_date || "") >= today) || null;
      return { team, fixture: next };
    });
  }, [clubTeams, schedule, today]);

  useEffect(() => {
    const ids = [
      ...new Set(nextByTeam.map((row) => row.fixture?.fixture_id).filter(Boolean)),
    ];
    if (!ids.length) return undefined;
    let cancelled = false;
    Promise.all(ids.map((id) => fetchLeagueFixtureRsvps(id).catch(() => []))).then(
      (rows) => {
        if (cancelled) return;
        const map = {};
        ids.forEach((id, i) => {
          map[id] = Array.isArray(rows[i]) ? rows[i] : [];
        });
        setRsvpsByFixture(map);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [nextByTeam]);

  useEffect(() => {
    const map = {};
    for (const { team, fixture } of nextByTeam) {
      const tn = Math.max(1, Number(team.team_number) || 1);
      const rows = fixture ? rsvpsByFixture[fixture.fixture_id] || [] : [];
      map[tn] = presenceOutsFromRsvps(rows);
    }
    setPresenceOutsByTeam(map);
  }, [nextByTeam, rsvpsByFixture]);

  const commitTeamCount = async (n) => {
    const next = Math.max(0, Math.min(20, Number(n) || 0));
    setTeamCount(next);
    setBusy(true);
    setMsg("");
    setError("");
    try {
      await setClubChampionshipTeamCount(next);
      const view = await fetchSeniorsTeams({ lang, reform: true });
      applyView(view);
      setMsg(
        t("competitionsTeamsUpdated", "Teams updated — line-ups rebuilt by ranking.")
      );
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSeasonStatus = async (playerUserId, status) => {
    const competitionId = teams?.competition?.competition_id;
    if (!competitionId || busy) return;
    setBusy(true);
    setError("");
    try {
      const view = await setCompetitionParticipation(competitionId, playerUserId, status);
      applyView(view);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onMatchRsvp = async (fixtureId, playerUserId, status) => {
    if (!fixtureId || busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await setLeagueFixtureRsvp(fixtureId, status, { playerUserId });
      setRsvpsByFixture((prev) => ({
        ...prev,
        [fixtureId]: result?.rsvps || [],
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onCreateTournament = async () => {
    if (!tourName.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      await createCompetition({
        kind: tourKind,
        format: "individual",
        name: tourName.trim(),
        category: tourCategory.trim() || null,
      });
      setTourName("");
      const comps = await listCompetitions();
      setList(comps || []);
      setTab("tournaments");
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (competitionId) => {
    if (busy) return;
    setBusy(true);
    try {
      await updateCompetition(competitionId, { status: "archived" });
      const comps = await listCompetitions();
      setList(comps || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const teamSize = Number(teams?.competition?.team_size || 4);
  const tournaments = (list || []).filter(
    (c) => c.kind === "tournament" || c.kind === "cup"
  );
  const championships = (list || []).filter((c) => c.kind === "championship");

  return (
    <AdminLayout>
      <div className={`comp-ed comp-ed--${mode}`}>
      {composerOpen ? (
        <TournamentSetupComposer
          open={composerOpen}
          mode={themeMode === "light" ? "light" : "dark"}
          onClose={() => setComposerOpen(false)}
          onCreated={() => {
            load();
          }}
        />
      ) : null}

        <header className="comp-ed__hero">
          <p className="comp-ed__kicker">
            <Trophy size={14} strokeWidth={2.2} aria-hidden />
            {t("competitionsEditorKicker", "Club editor")}
          </p>
          <h1>{t("competitionsEditorTitle", "Competitions")}</h1>
          <p className="comp-ed__lede">
            {t(
              "competitionsEditorLede",
              "Set how many championship teams you field, call players up when someone is out, and keep cups next to the national championship."
            )}
          </p>
          {matchLead ? (
            <p className="comp-ed__lead">
              {t(
                "competitionsMatchLead",
                "You are the matches & results lead — you can set presence for the next match and for the half-season."
              )}
            </p>
          ) : null}
        </header>

        <nav className="comp-ed__tabs" role="tablist" aria-label={t("competitionsEditorTitle", "Competitions")}>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "championship"}
            className={tab === "championship" ? "is-active" : ""}
            onClick={() => setTab("championship")}
          >
            <Users size={15} strokeWidth={2.2} aria-hidden />
            {t("competitionChampionship", "Championship")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "next"}
            className={tab === "next" ? "is-active" : ""}
            onClick={() => setTab("next")}
          >
            <CalendarDays size={15} strokeWidth={2.2} aria-hidden />
            {t("competitionNextMatch", "Next match")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "tournaments"}
            className={tab === "tournaments" ? "is-active" : ""}
            onClick={() => setTab("tournaments")}
          >
            <Medal size={15} strokeWidth={2.2} aria-hidden />
            {t("competitionTournaments", "Tournaments")}
            {tournaments.length ? <em>{tournaments.length}</em> : null}
          </button>
        </nav>

        {error ? <p className="comp-ed__error">{error}</p> : null}
        {msg ? <p className="comp-ed__ok">{msg}</p> : null}

        {tab === "championship" ? (
          <>
            <section className="comp-ed__card" aria-labelledby="comp-ed-count">
              <h2 id="comp-ed-count">{t("competitionsTeamCount", "Club teams")}</h2>
              <p className="comp-ed__hint">
                {t(
                  "competitionsTeamCountHint",
                  "Each team has 4 seats, filled by ranking points. Changing the number rebuilds Team 1…N immediately."
                )}
              </p>
              <div className="comp-ed__stepper">
                <button
                  type="button"
                  aria-label={t("competitionsFewerTeams", "Fewer teams")}
                  disabled={busy || teamCount <= 0}
                  onClick={() => commitTeamCount(teamCount - 1)}
                >
                  <Minus size={18} />
                </button>
                <strong>{teamCount}</strong>
                <button
                  type="button"
                  aria-label={t("competitionsMoreTeams", "More teams")}
                  disabled={busy || teamCount >= 20}
                  onClick={() => commitTeamCount(teamCount + 1)}
                >
                  <Plus size={18} />
                </button>
              </div>
            </section>

            <div className="comp-ed__teams">
              {championshipTeams.map((tm) => (
                <section key={tm.team_number} className="comp-ed__team">
                  <header>
                    <h3>{tm.label}</h3>
                    <span>
                      {tm.players?.length || 0}/{teamSize}
                    </span>
                  </header>
                  <ul>
                    {(tm.players || []).map((p) => (
                      <li key={p.user_id}>
                        <Avatar player={p} />
                        <span className="comp-ed__who">
                          <strong>{p.name}</strong>
                          <em>
                            {[
                              p.ranking_points != null ? `${p.ranking_points} pts` : null,
                              p.classement,
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                          </em>
                        </span>
                        <span className="comp-ed__acts">
                          <button
                            type="button"
                            className="comp-ed__ghost"
                            disabled={busy}
                            title={t(
                              "competitionsMarkUnknown",
                              "Unknown — reserve seat for next available"
                            )}
                            onClick={() => onSeasonStatus(p.user_id, "unknown")}
                          >
                            {t("competitionsUnknownShort", "Unknown")}
                          </button>
                          <button
                            type="button"
                            className="comp-ed__ghost"
                            disabled={busy}
                            title={t("competitionsOutThisHalf", "Out this half")}
                            onClick={() => onSeasonStatus(p.user_id, "absent")}
                          >
                            {t("competitionHalfOutShort", "Half")}
                          </button>
                        </span>
                      </li>
                    ))}
                    {Array.from({
                      length: Math.max(0, teamSize - (tm.players?.length || 0)),
                    }).map((_, i) => (
                      <li key={`empty-${tm.team_number}-${i}`} className="is-empty">
                        <span className="comp-ed__ava comp-ed__ava--ini">+</span>
                        <span className="comp-ed__who">
                          <strong>{t("competitionsEmptySeat", "Open seat")}</strong>
                          <em>{t("competitionsNextFills", "Next available player fills this")}</em>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>

            {(teams?.reserve || []).length ? (
              <section className="comp-ed__card">
                <h2>{t("competitionsReserve", "Next available")}</h2>
                <p className="comp-ed__hint">
                  {t(
                    "competitionsReserveHint",
                    "These players move up the moment someone in a team is out for a match or for the half-season."
                  )}
                </p>
                <ul className="comp-ed__people">
                  {teams.reserve.map((p) => (
                    <li key={p.user_id}>
                      <Avatar player={p} />
                      <span className="comp-ed__who">
                        <strong>{p.name}</strong>
                        <em>
                          {p.ranking_points != null ? `${p.ranking_points} pts` : t("competitionsReserve", "Next available")}
                        </em>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {(teams?.unknown || []).length ? (
              <section className="comp-ed__card">
                <h2>{t("competitionsUnknown", "Unknown")}</h2>
                <p className="comp-ed__hint">
                  {t(
                    "competitionsUnknownHint",
                    "Seat held for the next available player. Clear Unknown to put them back in the ranking pool."
                  )}
                </p>
                <ul className="comp-ed__people">
                  {teams.unknown.map((p) => (
                    <li key={p.user_id}>
                      <Avatar player={p} />
                      <span className="comp-ed__who">
                        <strong>{p.name}</strong>
                        <em>
                          {t(
                            "competitionsNextFills",
                            "Next available player fills this"
                          )}
                        </em>
                      </span>
                      <button
                        type="button"
                        className="comp-ed__ghost"
                        disabled={busy}
                        title={t("competitionsBackIn", "Back in")}
                        onClick={() => onSeasonStatus(p.user_id, "pending")}
                      >
                        {t("competitionsBackInShort", "Back")}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {(teams?.absent || []).length ? (
              <section className="comp-ed__card">
                <h2>{t("competitionsMidSeasonOut", "Out this half-season")}</h2>
                <ul className="comp-ed__people">
                  {teams.absent.map((p) => (
                    <li key={p.user_id}>
                      <Avatar player={p} />
                      <span className="comp-ed__who">
                        <strong>{p.name}</strong>
                        <em>{t("competitionsMidSeasonOut", "Out this half-season")}</em>
                      </span>
                      <button
                        type="button"
                        className="comp-ed__ghost"
                        disabled={busy}
                        title={t("competitionsBackIn", "Back in")}
                        onClick={() => onSeasonStatus(p.user_id, "pending")}
                      >
                        {t("competitionsBackInShort", "Back")}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}

        {tab === "next" ? (
          <div className="comp-ed__next-list">
            {nextByTeam.map(({ team, fixture }) => {
              const rsvps = fixture ? rsvpsByFixture[fixture.fixture_id] || [] : [];
              const ranking = availableForMatch(teams, []);
              const cascaded = cascadeMatchSquad(
                ranking,
                team.team_number,
                {
                  ...presenceOutsByTeam,
                  [Math.max(1, Number(team.team_number) || 1)]: presenceOutsFromRsvps(rsvps),
                },
                teamSize
              );
              const lineup = cascaded.squad;
              const out = playersOutThisMatch(teams, rsvps, team.team_number);
              const nextUp = cascaded.remainingAfter[0] || null;
              const opp =
                fixture?.home?.league_team_id === team.league_team_id
                  ? fixture?.away
                  : fixture?.home;
              return (
                <section key={team.league_team_id || team.team_number} className="comp-ed__card">
                  <header className="comp-ed__match-head">
                    <div>
                      <p className="comp-ed__kicker">{team.display_name || team.label}</p>
                      <h2>
                        {fixture
                          ? `${t("competitionVs", "vs")} ${opp?.display_name || "—"}`
                          : t("competitionNoFixtures", "No matches for this team yet.")}
                      </h2>
                      {fixture ? (
                        <p className="comp-ed__hint">
                          {formatMatchDate(fixture.match_date, locale)}
                          {fixture.kickoff_time ? ` · ${fixture.kickoff_time}` : ""}
                          {fixture.division ? ` · ${fixture.division}` : ""}
                        </p>
                      ) : null}
                    </div>
                    {fixture ? (
                      <button
                        type="button"
                        className="comp-ed__primary"
                        disabled={busy}
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const opened = await openChampionshipMatch(fixture.fixture_id);
                            const id = opened?.match?.championship_match_id;
                            if (id) navigate(`/competition/match/${id}`);
                          } catch (err) {
                            setError(err?.response?.data?.message || err.message);
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        {t("cmRecordMatch", "Record match")}
                      </button>
                    ) : null}
                  </header>

                  <h3 className="comp-ed__sub">{t("competitionLineup", "Line-up")}</h3>
                  <ul className="comp-ed__people">
                    {lineup.map((p) => (
                      <li
                        key={p.user_id}
                        className={isCalledUp(p, team.team_number) ? "is-called" : ""}
                      >
                        <Avatar player={p} />
                        <span className="comp-ed__who">
                          <strong>{p.name}</strong>
                          <em className={isCalledUp(p, team.team_number) ? "comp-ed__badge" : ""}>
                            {isCalledUp(p, team.team_number)
                              ? t("competitionsCalledUp", "Called up")
                              : t("competitionsPresent", "Present")}
                          </em>
                        </span>
                        {fixture ? (
                          <button
                            type="button"
                            className="comp-ed__ghost"
                            disabled={busy}
                            title={t("competitionsOutThisMatch", "Out this match")}
                            onClick={() => onMatchRsvp(fixture.fixture_id, p.user_id, "out")}
                          >
                            <UserMinus size={14} aria-hidden />
                            {t("competitionMatchOutShort", "Match")}
                          </button>
                        ) : null}
                      </li>
                    ))}
                    {Array.from({ length: emptySeats(lineup, teamSize) }).map((_, i) => (
                      <li key={`gap-${i}`} className="is-empty">
                        <span className="comp-ed__ava comp-ed__ava--ini">+</span>
                        <span className="comp-ed__who">
                          <strong>{t("competitionsEmptySeat", "Open seat")}</strong>
                        </span>
                      </li>
                    ))}
                  </ul>

                  {out.length ? (
                    <>
                      <h3 className="comp-ed__sub">{t("competitionsWontPlay", "Won’t play this match")}</h3>
                      <ul className="comp-ed__people">
                        {out.map((p) => (
                          <li key={p.user_id} className="is-out">
                            <Avatar player={p} />
                            <span className="comp-ed__who">
                              <strong>{p.name}</strong>
                              <em>{t("competitionsOutThisMatch", "Out this match")}</em>
                            </span>
                            {fixture ? (
                              <button
                                type="button"
                                className="comp-ed__primary"
                                disabled={busy}
                                title={t("competitionsBackIn", "Back in")}
                                onClick={() =>
                                  onMatchRsvp(fixture.fixture_id, p.user_id, "pending")
                                }
                              >
                                <Check size={14} aria-hidden />
                                {t("competitionsBackInShort", "Back")}
                              </button>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}

                  {nextUp ? (
                    <p className="comp-ed__nextup">
                      {t("competitionsNextAvailable", "Next available")}: <strong>{nextUp.name}</strong>
                    </p>
                  ) : null}
                </section>
              );
            })}
            {!nextByTeam.length ? (
              <section className="comp-ed__card">
                <h2>{t("competitionNoFixtures", "No matches for this team yet.")}</h2>
                <p className="comp-ed__hint">
                  {t(
                    "competitionEmptyCoachGeneric",
                    "Ask Pingflo to load the championship calendar when it is available."
                  )}
                </p>
              </section>
            ) : null}
          </div>
        ) : null}

        {tab === "tournaments" ? (
          <>
            <section className="comp-ed__card">
              <h2>{t("competitionsAddTournament", "Add a tournament")}</h2>
              <button type="button" className="comp-ed__btn" onClick={() => setComposerOpen(true)}>
                {t("competitionsTournamentSetup", "Tournament setup")}
              </button>
              <div className="comp-ed__form">
                <label>
                  {t("competitionsTournamentName", "Name")}
                  <input
                    value={tourName}
                    onChange={(e) => setTourName(e.target.value)}
                    placeholder={t("competitionsTournamentNamePh", "Open, cup, ranking…")}
                  />
                </label>
                <label>
                  {t("competitionsTournamentCategory", "Category")}
                  <input
                    value={tourCategory}
                    onChange={(e) => setTourCategory(e.target.value)}
                    placeholder="seniors"
                  />
                </label>
                <label>
                  {t("competitionKindTournament", "Tournament")}
                  <select value={tourKind} onChange={(e) => setTourKind(e.target.value)}>
                    <option value="tournament">{t("competitionKindTournament", "Tournament")}</option>
                    <option value="cup">{t("competitionKindCup", "Cup")}</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="comp-ed__primary"
                  disabled={busy || !tourName.trim()}
                  onClick={onCreateTournament}
                >
                  {t("competitionsCreateTournament", "Create")}
                </button>
              </div>
            </section>

            {championships.map((c) => (
              <article key={c.competition_id} className="comp-ed__event is-champ">
                <span className="comp-ed__event-icon" aria-hidden>
                  <Trophy size={20} />
                </span>
                <span>
                  <strong>{c.name}</strong>
                  <em>
                    {t("competitionKindChampionship", "Championship")}
                    {c.category ? ` · ${c.category}` : ""}
                    {c.season_label ? ` · ${c.season_label}` : ""}
                  </em>
                </span>
              </article>
            ))}

            {tournaments.map((c) => (
              <article key={c.competition_id} className={`comp-ed__event kind-${c.kind}`}>
                <span className="comp-ed__event-icon" aria-hidden>
                  <Medal size={20} />
                </span>
                <span>
                  <strong>{c.name}</strong>
                  <em>
                    {c.kind === "cup"
                      ? t("competitionKindCup", "Cup")
                      : t("competitionKindTournament", "Tournament")}
                    {c.category ? ` · ${c.category}` : ""}
                    {` · ${
                      c.format === "teams"
                        ? t("competitionFormatTeams", "Teams")
                        : t("competitionFormatIndividual", "Individual")
                    }`}
                    {c.season_label ? ` · ${c.season_label}` : ""}
                  </em>
                </span>
                {c.status !== "archived" ? (
                  <button
                    type="button"
                    className="comp-ed__ghost"
                    disabled={busy}
                    onClick={() => onArchive(c.competition_id)}
                  >
                    {t("competitionsArchive", "Archive")}
                  </button>
                ) : (
                  <span className="pill">{t("competitionStatusArchived", "Archived")}</span>
                )}
              </article>
            ))}
          </>
        ) : null}
      </div>
    </AdminLayout>
  );
}
